/* eslint-disable react-hooks/exhaustive-deps */
import axios from 'axios';
import React from 'react';
import { useSelector } from 'react-redux';
import { update } from '../../services/item';
import { updateField } from '../../services/item';
import { deepCloneArray } from '../../utils/array';
import { LinkFull } from './link.Full';
import { LinkExpandable } from './link.Expandable';
import { LinkOneLine } from './link.OneLine';
import { LinkShort } from './link.Short';
import { LinkCard } from './link.Card';
import { LinkGlobalSettings } from './link.Global.Settings';
import { LinkFullSettings } from './link.Full.Settings';
import { canEditItemForItem } from '../../utils/item';
import { canDisplayLinkedItem } from '../../utils/permissions';
import { syncAutoTabsOnLinkRemoved } from '../../utils/item';
import { FaLock } from 'react-icons/fa';
import useItems from '../../hooks/useItems';
import { useBoardReadOnlyMode } from '../../context/boardReadOnly';

const isValidObjectId = id =>
  typeof id === 'string' && /^[a-f0-9]{24}$/i.test(id);

export const LinkGlobal = props => {
  const {
    linkId,
    itemId,
    displayType,
    elementsToDisplay,
    fromBoard,
    getTextHeight,
    fieldId,
    gridLayoutKey,
    canEdit: canEditProp,
    enableDrag = true,
    fullTabId,
    noteNumEnabled,
    noteNumPosition,
    noteNum,
    onChangeNoteNum,
    noteCanEdit,
    reducedMinAvatarSize,
  } = props;

  const itemIds = [itemId, linkId];
  const [item, link] = useItems(itemIds);
  const [linkCampaign, linkWorld, linkUniverse] = useItems([
    link?.campaign,
    link?.world,
    link?.universe,
  ]);
  const isMobile = useSelector(state => state.global.isMobile);
  const isTouch = useSelector(state => state.global.isTouch);
  const currentTab = useSelector(state => state.global.currentTab);
  const tab = useSelector(state => state.items?.[currentTab]);
  const items = useSelector(state => state.items);
  const listCampaignsId = useSelector(state => state.global.listCampaignsId);
  const inheritedBoardReadOnlyMode = useBoardReadOnlyMode();
  const canEdit =
    inheritedBoardReadOnlyMode === 'hard'
      ? false
      : canEditProp ?? canEditItemForItem(item, items);
  const hasMissingAccessScope =
    (link?.type !== 'campaign' &&
      isValidObjectId(link?.campaign) &&
      !linkCampaign?._id) ||
    (isValidObjectId(link?.world) && !linkWorld?._id) ||
    (isValidObjectId(link?.universe) && !linkUniverse?._id);

  const gridNoteConfig = React.useMemo(() => {
    if (!tab?.grid || !linkId) {
      return null;
    }
    const entry = tab.grid.find(element => element?.[linkId]);
    return entry ? entry[linkId] : null;
  }, [tab?.grid, linkId]);

  const effectiveNoteNumEnabled =
    typeof noteNumEnabled === 'boolean'
      ? noteNumEnabled
      : gridNoteConfig?.noteNumEnabled === true;
  const effectiveNoteNum =
    typeof noteNum === 'number' && Number.isFinite(noteNum)
      ? noteNum
      : typeof gridNoteConfig?.noteNum === 'number' &&
        Number.isFinite(gridNoteConfig.noteNum)
        ? gridNoteConfig.noteNum
        : 0;
  const effectiveNoteCanEdit =
    typeof noteCanEdit === 'boolean' ? noteCanEdit : canEdit;

  const handleChangeNoteNum =
    typeof onChangeNoteNum === 'function'
      ? onChangeNoteNum
      : value => {
          if (!tab) return;
          if (!effectiveNoteCanEdit) return;
          updateField(tab, tab?.type, value, linkId, 'noteNum');
        };

  const deleteLink = async linkId => {
    const clonedLinks = deepCloneArray(item.links);
    let idx = -1;
    for (let i = 0; i < clonedLinks.length; i++) {
      const text = clonedLinks[i];
      if (
        Object.keys(text)[0] === linkId[0] ||
        Object.keys(text)[0] === linkId
      ) {
        idx = i;
      }
    }

    if (idx > -1) {
      clonedLinks.splice(idx, 1);
      update(`items/${itemId}`, 'links', clonedLinks);
    }

    const linkUrl = `items/${linkId}`;
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/${linkUrl}`);
    const cloneLinkedLink = deepCloneArray(res.data.links);
    let idxLink = -1;
    for (let i = 0; i < cloneLinkedLink.length; i++) {
      const text = cloneLinkedLink[i];
      if (Object.keys(text)[0] === item._id) {
        idxLink = i;
      }
    }
    if (idxLink > -1) {
      cloneLinkedLink.splice(idxLink, 1);
      update(linkUrl, 'links', cloneLinkedLink);
    }
    await syncAutoTabsOnLinkRemoved(item, res.data);
  };

  if (
    canDisplayLinkedItem({
      item: link,
      campaign: linkCampaign,
      world: linkWorld,
      universe: linkUniverse,
      items,
      listCampaignsId,
      hasMissingAccessScope,
    })
  ) {
    const dragPayload = {
      type: 'item',
      itemId: linkId,
      sourceFilterId: null,
    };
    return (
      <div
        className={`${getTextHeight ? '' : 'h-full overflow-hidden'} ${
          fromBoard && enableDrag ? 'board-grid-no-drag' : ''
        }`}
        draggable={enableDrag && canEdit && !isTouch}
        onDragStart={event => {
          if (!enableDrag || !canEdit || isTouch) return;
          const serialized = JSON.stringify(dragPayload);
          window.__estynossDragPayload = dragPayload;
          event.dataTransfer.setData(
            'application/x-estynoss-item',
            serialized
          );
          event.dataTransfer.setData('text/plain', serialized);
          event.dataTransfer.effectAllowed = 'move';
          window.__filterDropTargetId = null;
        }}
        onDragEnd={() => {
          window.setTimeout(() => {
            window.__estynossDragPayload = null;
          }, 150);
        }}
      >
        {displayType === 'réduit' && canEdit && (
          <LinkGlobalSettings
            itemId={itemId}
            linkId={linkId}
          />
        )}
        {(displayType === 'complet' || displayType === 'complet dynamique') &&
          canEdit && <LinkFullSettings linkId={linkId} />}
        {displayType === 'expandable' && (
          <LinkExpandable
            linkId={linkId}
            itemId={itemId}
            deleteLink={isMobile || !canEdit ? null : deleteLink}
          />
        )}
        {(displayType === 'link' || displayType === 'simple') && (
          <LinkOneLine linkId={linkId} />
        )}
        {(displayType === 'complet' || displayType === 'complet dynamique') && (
          <LinkFull
            linkId={linkId}
            getTextHeight={getTextHeight}
            fieldId={fieldId}
            parentItemId={itemId}
            fullTabId={fullTabId}
            gridLayoutKey={gridLayoutKey}
          />
        )}
        {displayType === 'réduit' && (
          <LinkShort
            linkId={linkId}
            elementsToDisplay={elementsToDisplay}
            noteNumEnabled={effectiveNoteNumEnabled}
            noteNumPosition={noteNumPosition}
            noteNum={effectiveNoteNum}
            onChangeNoteNum={handleChangeNoteNum}
            noteCanEdit={effectiveNoteCanEdit}
            reducedMinAvatarSize={reducedMinAvatarSize}
          />
        )}
        {displayType === 'carte' && <LinkCard linkId={linkId} />}
      </div>
    );
  } else {
    if (fromBoard) {
      return (
        <div className='text-secondary w-full h-full flex items-center justify-center opacity-25 border-secondary border'>
          <FaLock size={20} />
        </div>
      );
    } else {
      return <></>;
    }
  }
};
